import { useState, useMemo } from "react";

// ─── THEME TOKENS ────────────────────────────────────────────────────────────
const LIGHT = {
  bg:          "#FAF7F2",
  surface:     "#FFFFFF",
  card:        "#FFFCF8",
  cardHover:   "#FFF5EE",
  border:      "#EAD9CC",
  borderLight: "#F0E4D8",
  primary:     "#B5705A",   // dusty rose-terracotta
  primaryHov:  "#9C5C47",
  accent:      "#7A9E87",   // sage green
  accentLight: "#A8C5B0",
  gold:        "#C4973A",
  text:        "#2C1A12",
  textSub:     "#7A5C4E",
  textMuted:   "#AE998F",
  success:     "#5A8F70",
  warn:        "#C49040",
  danger:      "#B84040",
  white:       "#FFFFFF",
  shadow:      "0 2px 16px rgba(44,26,18,0.08)",
  shadowMd:    "0 4px 32px rgba(44,26,18,0.12)",
  navBorder:   "#EAD9CC",
  heroBg:      "linear-gradient(135deg, #FFF0E8 0%, #FAF7F2 55%, #EDF4F0 100%)",
  chipActive:  "#B5705A22",
  chipBorder:  "#B5705A",
  chipText:    "#B5705A",
  statBg:      "#FFF5F0",
  statBorder:  "#EAD9CC",
  inputBg:     "#FFFCF8",
  modalOverlay:"rgba(44,26,18,0.45)",
  calHeader:   "#FFF8F4",
  calDayBg:    "#FFFCF8",
  calToday:    "#B5705A",
  calSelected: "#7A9E87",
  toggle:      "#B5705A",
};

const DARK = {
  bg:          "#0D1B2A",
  surface:     "#112236",
  card:        "#16304A",
  cardHover:   "#1A3A58",
  border:      "#1E4A6E",
  borderLight: "#2A5F8F",
  primary:     "#4A90C4",
  primaryHov:  "#2C6B9E",
  accent:      "#2DB87A",
  accentLight: "#6DAFD9",
  gold:        "#C9963A",
  text:        "#E8F4FC",
  textSub:     "#7FB3D3",
  textMuted:   "#4A7A9B",
  success:     "#2DB87A",
  warn:        "#E8A21A",
  danger:      "#D94040",
  white:       "#FFFFFF",
  shadow:      "0 2px 16px rgba(0,10,20,0.4)",
  shadowMd:    "0 4px 32px rgba(0,10,20,0.5)",
  navBorder:   "#1E4A6E",
  heroBg:      "linear-gradient(135deg, #1A3A58 0%, #112236 55%, #0D2035 100%)",
  chipActive:  "#4A90C422",
  chipBorder:  "#4A90C4",
  chipText:    "#6DAFD9",
  statBg:      "#0D1B2A",
  statBorder:  "#1E4A6E",
  inputBg:     "#112236",
  modalOverlay:"rgba(0,10,20,0.85)",
  calHeader:   "#16304A",
  calDayBg:    "#112236",
  calToday:    "#E8541A",
  calSelected: "#4A90C4",
  toggle:      "#4A90C4",
};

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const MEAL_TYPES  = ["Breakfast","Brunch","Lunch","Dinner","Cocktail Party","Tasting Menu"];
const CUISINE_TYPES = ["American","Italian","French","Japanese","Mediterranean","Mexican","Indian","Thai","Farm-to-Table","Modern Fusion"];
const DIETARY     = ["Vegan","Vegetarian","Gluten-Free","Dairy-Free","Nut-Free","Kosher","Halal","Pescatarian"];
const SEATING     = ["Communal Table","Intimate Dining","Outdoor Garden","Kitchen Counter","Formal Dining","Cocktail Standing"];
const AVATAR_COLS_LIGHT = ["#B5705A","#7A9E87","#C4973A","#8C6BAE","#4A8FA8","#C4787A","#5A8F70","#C49040"];
const AVATAR_COLS_DARK  = ["#2C6B9E","#E8541A","#2DB87A","#C9963A","#7B4FD4","#D94040","#1A8C8C","#8C5A1A"];

const SAMPLE_MENUS = {
  "Dinner":         ["Oysters on the Half Shell","Heirloom Tomato Bisque","Grilled Wagyu Tenderloin","Truffle Pomme Purée","Chocolate Fondant"],
  "Brunch":         ["Smoked Salmon Blini","Avocado Benedict","Ricotta Pancakes","Seasonal Fruit Board","Lavender Crème Brûlée"],
  "Lunch":          ["Burrata & Stone Fruit","Seared Tuna Niçoise","Duck Confit Sandwich","Herb Roasted Potatoes","Lemon Tart"],
  "Breakfast":      ["Granola Parfait","Shakshuka","Brioche French Toast","Cold Brew Station","Seasonal Pastries"],
  "Cocktail Party": ["Caviar Blinis","Wagyu Sliders","Truffle Arancini","Smoked Duck Crostini","Petit Fours"],
  "Tasting Menu":   ["Amuse-Bouche","Cured Salmon","Mushroom Velouté","Citrus Sorbet","Duck Two Ways","Cheese Course","Deconstructed Dessert"],
};

const genId   = () => Math.random().toString(36).slice(2,9);
const today   = new Date();
const todayStr= `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;
const offsetDate=(n)=>{ const d=new Date(); d.setDate(d.getDate()+n); return d.toISOString().split("T")[0]; };
const getDaysInMonth  = (y,m) => new Date(y,m+1,0).getDate();
const getFirstDay     = (y,m) => new Date(y,m,1).getDay();
const getInitials     = (n)   => n.split(" ").map(x=>x[0]).join("").toUpperCase().slice(0,2);
const fmtDate         = (d)   => { if(!d)return""; const[y,m,day]=d.split("-"); return new Date(y,m-1,day).toLocaleDateString("en-US",{weekday:"short",month:"long",day:"numeric"}); };
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_NAMES   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

// ─── SEED DATA ────────────────────────────────────────────────────────────────
const mkGuests = (dark) => [
  {id:"g1",name:"Sophia Hartley",email:"s.hartley@email.com",phone:"",dietary:["Gluten-Free"],rsvp:"confirmed",note:"Best friend",avatar:(dark?AVATAR_COLS_DARK:AVATAR_COLS_LIGHT)[0],addedAt:""},
  {id:"g2",name:"Camille Renaud",email:"",phone:"+1 555-0102",dietary:[],rsvp:"confirmed",note:"",avatar:(dark?AVATAR_COLS_DARK:AVATAR_COLS_LIGHT)[1],addedAt:""},
  {id:"g3",name:"Priya Mehta",email:"priya@email.com",phone:"",dietary:["Vegetarian"],rsvp:"pending",note:"",avatar:(dark?AVATAR_COLS_DARK:AVATAR_COLS_LIGHT)[2],addedAt:""},
];

const mkEvents = (dark) => [
  {id:"e1",title:"Spring Supper at the Garden Table",date:offsetDate(5),startTime:"18:30",endTime:"21:30",mealType:"Dinner",cuisine:"Farm-to-Table",seating:"Outdoor Garden",pricePerGuest:"85",maxGuests:6,status:"confirmed",dietary:["Vegetarian","Gluten-Free"],menuItems:["Heirloom Tomato Bisque","Grilled Wagyu Tenderloin","Truffle Pomme Purée","Chocolate Fondant"],notes:"Street parking available. Business casual suggested.",guests:mkGuests(dark),createdAt:""},
  {id:"e2",title:"Sunday Morning Brunch Circle",date:offsetDate(12),startTime:"10:00",endTime:"13:00",mealType:"Brunch",cuisine:"French",seating:"Communal Table",pricePerGuest:"45",maxGuests:8,status:"pending",dietary:["Vegan","Dairy-Free"],menuItems:["Smoked Salmon Blini","Avocado Benedict","Ricotta Pancakes","Lavender Crème Brûlée"],notes:"Garden setting — bring a light jacket.",guests:[{id:"g4",name:"Ines Beaumont",email:"ines@email.com",phone:"",dietary:["Vegan"],rsvp:"confirmed",note:"",avatar:(dark?AVATAR_COLS_DARK:AVATAR_COLS_LIGHT)[3],addedAt:""},{id:"g5",name:"Margot Ellis",email:"m.ellis@email.com",phone:"",dietary:[],rsvp:"pending",note:"",avatar:(dark?AVATAR_COLS_DARK:AVATAR_COLS_LIGHT)[4],addedAt:""}],createdAt:""},
];

// ─── TOGGLE BUTTON ────────────────────────────────────────────────────────────
function ThemeToggle({ dark, onToggle, T }) {
  return (
    <button onClick={onToggle} style={{
      display:"flex",alignItems:"center",gap:8,padding:"7px 14px",border:`1.5px solid ${T.border}`,borderRadius:20,
      background:T.surface,color:T.textSub,fontSize:13,fontWeight:600,cursor:"pointer",transition:"all 0.2s",fontFamily:"inherit",
    }}>
      <span style={{fontSize:16}}>{dark?"☀":"🌙"}</span>
      {dark ? "Light Mode" : "Dark Mode"}
    </button>
  );
}

// ─── CALENDAR ────────────────────────────────────────────────────────────────
function CalendarView({ events, selectedDate, setSelectedDate, viewYear, viewMonth, setViewMonth, setViewYear, T, dark }) {
  const daysInMonth = getDaysInMonth(viewYear,viewMonth);
  const firstDay    = getFirstDay(viewYear,viewMonth);
  const cells = [];
  for(let i=0;i<firstDay;i++) cells.push(null);
  for(let d=1;d<=daysInMonth;d++) cells.push(d);
  const ds = (d) => `${viewYear}-${String(viewMonth+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  const prevMonth = () => viewMonth===0 ? (setViewMonth(11),setViewYear(y=>y-1)) : setViewMonth(m=>m-1);
  const nextMonth = () => viewMonth===11? (setViewMonth(0), setViewYear(y=>y+1)) : setViewMonth(m=>m+1);

  return (
    <div style={{border:`1px solid ${T.border}`,borderRadius:16,overflow:"hidden",boxShadow:T.shadow}}>
      <div style={{background:T.calHeader,padding:"14px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`1px solid ${T.border}`}}>
        <button onClick={prevMonth} style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:8,padding:"5px 12px",color:T.textSub,cursor:"pointer",fontSize:16}}>‹</button>
        <span style={{fontWeight:700,fontSize:15,color:T.text,fontFamily:"'Cormorant Garamond',serif",letterSpacing:"0.03em"}}>{MONTH_NAMES[viewMonth]} {viewYear}</span>
        <button onClick={nextMonth} style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:8,padding:"5px 12px",color:T.textSub,cursor:"pointer",fontSize:16}}>›</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",background:T.border}}>
        {DAY_NAMES.map(d=>(
          <div key={d} style={{background:T.calHeader,padding:"8px 4px",textAlign:"center",fontSize:11,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em"}}>{d}</div>
        ))}
        {cells.map((d,i)=>{
          if(!d) return <div key={`e${i}`} style={{background:T.bg,minHeight:70}} />;
          const dateStr = ds(d);
          const dayEvs  = events.filter(e=>e.date===dateStr);
          const isPast  = dateStr < todayStr;
          const isTod   = dateStr === todayStr;
          const isSel   = dateStr === selectedDate;
          return (
            <div key={dateStr} onClick={()=>!isPast&&setSelectedDate(dateStr)}
              style={{background:isSel?T.calSelected+(dark?"":"33"):isPast?T.bg:T.calDayBg,
                minHeight:70,padding:"8px 6px",cursor:isPast?"default":"pointer",
                opacity:isPast?0.45:1,transition:"background 0.15s",boxSizing:"border-box"}}>
              <div style={{width:24,height:24,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:4,
                background:isTod?T.calToday:isSel?(dark?T.calSelected:"#7A9E87"):"transparent",
                color:isTod||isSel?T.white:T.text,fontSize:12,fontWeight:isTod?700:500}}>
                {d}
              </div>
              {dayEvs.slice(0,2).map(ev=>(
                <div key={ev.id} style={{fontSize:9,borderRadius:3,padding:"2px 4px",marginBottom:2,
                  background:ev.status==="confirmed"?T.success+"33":T.warn+"33",
                  color:ev.status==="confirmed"?T.success:T.warn,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  {ev.mealType}
                </div>
              ))}
              {dayEvs.length>2&&<div style={{fontSize:9,color:T.textMuted}}>+{dayEvs.length-2}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── GUEST ROW ────────────────────────────────────────────────────────────────
function GuestRow({g,onRemove,onToggleRsvp,T}){
  return(
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:T.surface,borderRadius:10,marginBottom:8,border:`1px solid ${T.border}`}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <div style={{width:34,height:34,borderRadius:"50%",background:g.avatar,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:"#fff",flexShrink:0}}>{getInitials(g.name)}</div>
        <div>
          <div style={{fontWeight:600,fontSize:14,color:T.text}}>{g.name}</div>
          <div style={{fontSize:11,color:T.textMuted}}>{g.email||g.phone||"No contact"}</div>
          {g.dietary.length>0&&<div style={{display:"flex",gap:4,marginTop:3,flexWrap:"wrap"}}>{g.dietary.map(d=><span key={d} style={{background:T.gold+"22",color:T.gold,border:`1px solid ${T.gold}44`,borderRadius:20,padding:"2px 8px",fontSize:10,fontWeight:600}}>{d}</span>)}</div>}
        </div>
      </div>
      <div style={{display:"flex",gap:6,alignItems:"center"}}>
        <span onClick={()=>onToggleRsvp(g.id)} style={{cursor:"pointer",background:g.rsvp==="confirmed"?T.success+"22":T.warn+"22",color:g.rsvp==="confirmed"?T.success:T.warn,border:`1px solid ${g.rsvp==="confirmed"?T.success+"55":T.warn+"55"}`,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700}}>
          {g.rsvp==="confirmed"?"✓ Confirmed":"Pending"}
        </span>
        <button onClick={()=>onRemove(g.id)} style={{background:"transparent",border:`1px solid ${T.danger}44`,borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:600,color:T.danger,cursor:"pointer"}}>×</button>
      </div>
    </div>
  );
}

// ─── GUEST MANAGER ────────────────────────────────────────────────────────────
function GuestManager({guests,setGuests,T,dark}){
  const [name,setName]=useState(""); const [email,setEmail]=useState(""); const [phone,setPhone]=useState(""); const [dietary,setDietary]=useState([]); const [note,setNote]=useState("");
  const AVAT = dark ? AVATAR_COLS_DARK : AVATAR_COLS_LIGHT;
  const add=()=>{
    if(!name.trim()||guests.length>=8) return;
    setGuests(p=>[...p,{id:genId(),name:name.trim(),email,phone,dietary,note,avatar:AVAT[p.length%AVAT.length],rsvp:"pending",addedAt:new Date().toISOString()}]);
    setName("");setEmail("");setPhone("");setDietary([]);setNote("");
  };
  const inp = (val,set,ph) => <input style={{width:"100%",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 13px",color:T.text,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"inherit"}} value={val} onChange={e=>set(e.target.value)} placeholder={ph} />;
  return(
    <div>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:20,marginBottom:14,boxShadow:T.shadow}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <span style={{fontWeight:700,fontSize:14,color:T.text}}>Add a Guest</span>
          <span style={{background:guests.length>=8?T.danger+"22":T.primary+"22",color:guests.length>=8?T.danger:T.primary,border:`1px solid ${guests.length>=8?T.danger+"55":T.primary+"55"}`,borderRadius:20,padding:"3px 12px",fontSize:12,fontWeight:700}}>{guests.length}/8 Seats</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div><label style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:4}}>Name *</label>{inp(name,setName,"Jane Smith")}</div>
          <div><label style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:4}}>Email</label>{inp(email,setEmail,"jane@email.com")}</div>
          <div><label style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:4}}>Phone</label>{inp(phone,setPhone,"+1 (555) 000-0000")}</div>
          <div><label style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:4}}>Note</label>{inp(note,setNote,"VIP, close friend…")}</div>
        </div>
        <div style={{marginBottom:12}}>
          <label style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:6}}>Dietary</label>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {DIETARY.map(d=><span key={d} onClick={()=>setDietary(p=>p.includes(d)?p.filter(x=>x!==d):[...p,d])} style={{padding:"4px 11px",borderRadius:20,fontSize:12,fontWeight:500,cursor:"pointer",border:`1px solid ${dietary.includes(d)?T.chipBorder:T.border}`,background:dietary.includes(d)?T.chipActive:"transparent",color:dietary.includes(d)?T.chipText:T.textMuted}}>{d}</span>)}
          </div>
        </div>
        <button onClick={add} disabled={guests.length>=8||!name.trim()} style={{background:T.primary,color:"#fff",border:"none",borderRadius:9,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:guests.length>=8||!name.trim()?"not-allowed":"pointer",opacity:guests.length>=8||!name.trim()?0.5:1,fontFamily:"inherit"}}>Add Guest</button>
      </div>
      {guests.length===0&&<div style={{textAlign:"center",color:T.textMuted,padding:28,border:`1.5px dashed ${T.border}`,borderRadius:12,fontSize:14}}>No guests added yet</div>}
      {guests.map(g=><GuestRow key={g.id} g={g} T={T} onRemove={id=>setGuests(p=>p.filter(x=>x.id!==id))} onToggleRsvp={id=>setGuests(p=>p.map(x=>x.id===id?{...x,rsvp:x.rsvp==="confirmed"?"pending":"confirmed"}:x))} />)}
      {guests.length>0&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginTop:12}}>
          {[[guests.filter(g=>g.rsvp==="confirmed").length,"Confirmed",T.success],[guests.filter(g=>g.rsvp==="pending").length,"Awaiting",T.warn],[8-guests.length,"Open Seats",T.primary]].map(([v,l,c])=>(
            <div key={l} style={{background:T.statBg,border:`1px solid ${T.statBorder}`,borderRadius:10,padding:"10px 0",textAlign:"center"}}>
              <div style={{fontSize:22,fontWeight:800,color:c}}>{v}</div>
              <div style={{fontSize:11,color:T.textMuted}}>{l}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── EVENT FORM ───────────────────────────────────────────────────────────────
function EventForm({onSave,onCancel,initial,T,dark}){
  const [form,setForm]=useState(initial||{title:"",date:"",startTime:"18:00",endTime:"21:00",mealType:"Dinner",cuisine:"Modern Fusion",seating:"Formal Dining",pricePerGuest:"",menuItems:[],dietary:[],notes:"",maxGuests:8,status:"pending",guests:[]});
  const [guests,setGuests]=useState(initial?.guests||[]);
  const [menuInput,setMenuInput]=useState("");
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const times=[];
  for(let h=6;h<=23;h++){times.push(`${String(h).padStart(2,"0")}:00`);times.push(`${String(h).padStart(2,"0")}:30`);}

  const inp = (val,onChange,ph,type="text") => <input type={type} style={{width:"100%",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 13px",color:T.text,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"inherit"}} value={val} onChange={e=>onChange(e.target.value)} placeholder={ph} />;
  const sel = (val,onChange,opts) => <select style={{width:"100%",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 13px",color:T.text,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"inherit",cursor:"pointer"}} value={val} onChange={e=>onChange(e.target.value)}>{opts.map(o=><option key={o}>{o}</option>)}</select>;
  const lbl = (t) => <label style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:5}}>{t}</label>;

  return(
    <div>
      <div style={{marginBottom:20}}>
        <div style={{fontSize:20,fontWeight:800,color:T.text,fontFamily:"'Cormorant Garamond',serif",letterSpacing:"-0.3px",marginBottom:3}}>{initial?.id?"Edit Event":"New Supper Event"}</div>
        <div style={{fontSize:13,color:T.textMuted}}>Fill in your hosting details</div>
      </div>
      <div style={{marginBottom:14}}>{lbl("Event Title *")}{inp(form.title,v=>set("title",v),"\"Sunday Farm Table Dinner\"")}</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
        <div>{lbl("Meal Type")}{sel(form.mealType,v=>set("mealType",v),MEAL_TYPES)}</div>
        <div>{lbl("Cuisine")}{sel(form.cuisine,v=>set("cuisine",v),CUISINE_TYPES)}</div>
        <div>{lbl("Date *")}{inp(form.date,v=>set("date",v),"","date")}</div>
        <div>{lbl("Seating Style")}{sel(form.seating,v=>set("seating",v),SEATING)}</div>
        <div>{lbl("Start Time")}{sel(form.startTime,v=>set("startTime",v),times)}</div>
        <div>{lbl("End Time")}{sel(form.endTime,v=>set("endTime",v),times)}</div>
        <div>{lbl("Max Guests (1–8)")}{sel(String(form.maxGuests),v=>set("maxGuests",Number(v)),["1","2","3","4","5","6","7","8"])}</div>
        <div>{lbl("Price Per Guest ($)")}{inp(form.pricePerGuest,v=>set("pricePerGuest",v),"0 = complimentary","number")}</div>
      </div>
      <div style={{marginBottom:14}}>
        {lbl("Dietary Accommodations")}
        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
          {DIETARY.map(d=><span key={d} onClick={()=>set("dietary",form.dietary.includes(d)?form.dietary.filter(x=>x!==d):[...form.dietary,d])} style={{padding:"4px 12px",borderRadius:20,fontSize:12,fontWeight:500,cursor:"pointer",border:`1px solid ${form.dietary.includes(d)?T.chipBorder:T.border}`,background:form.dietary.includes(d)?T.chipActive:"transparent",color:form.dietary.includes(d)?T.chipText:T.textMuted}}>{d}</span>)}
        </div>
      </div>
      <div style={{marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>{lbl("Menu / Courses")}<button onClick={()=>set("menuItems",SAMPLE_MENUS[form.mealType]||[])} style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:8,padding:"4px 12px",fontSize:11,color:T.textSub,cursor:"pointer",fontFamily:"inherit"}}>Load Sample</button></div>
        <div style={{display:"flex",gap:8,marginBottom:8}}>
          <input style={{flex:1,background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 13px",color:T.text,fontSize:13,outline:"none",fontFamily:"inherit"}} value={menuInput} onChange={e=>setMenuInput(e.target.value)} placeholder="Add a course or dish…" onKeyDown={e=>e.key==="Enter"&&menuInput.trim()&&(set("menuItems",[...form.menuItems,menuInput.trim()]),setMenuInput(""))} />
          <button onClick={()=>{if(menuInput.trim()){set("menuItems",[...form.menuItems,menuInput.trim()]);setMenuInput("");}}} style={{background:T.primary,color:"#fff",border:"none",borderRadius:9,padding:"9px 16px",fontSize:16,fontWeight:700,cursor:"pointer"}}>+</button>
        </div>
        {form.menuItems.map((item,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:`1px solid ${T.border}22`}}>
            <span style={{color:T.primary,fontSize:14}}>🍴</span>
            <span style={{flex:1,fontSize:13,color:T.text}}>{item}</span>
            <button onClick={()=>set("menuItems",form.menuItems.filter((_,idx)=>idx!==i))} style={{background:"transparent",border:"none",cursor:"pointer",color:T.danger,fontSize:18,lineHeight:1}}>×</button>
          </div>
        ))}
      </div>
      <div style={{marginBottom:18}}>
        {lbl("Host Notes")}
        <textarea style={{width:"100%",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 13px",color:T.text,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"inherit",minHeight:72,resize:"vertical"}} value={form.notes} onChange={e=>set("notes",e.target.value)} placeholder="Parking, dress code, what to bring…" />
      </div>
      <div style={{height:1,background:T.border,margin:"0 0 20px"}} />
      <div style={{marginBottom:20}}>
        <div style={{fontWeight:700,fontSize:15,color:T.primary,fontFamily:"'Cormorant Garamond',serif",marginBottom:14}}>Guest List</div>
        <GuestManager guests={guests} setGuests={setGuests} T={T} dark={dark} />
      </div>
      <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
        <button onClick={onCancel} style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 18px",fontSize:13,fontWeight:600,color:T.textSub,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
        <button onClick={()=>{if(form.title&&form.date)onSave({...form,id:initial?.id||genId(),guests,createdAt:initial?.createdAt||new Date().toISOString()});}} disabled={!form.title||!form.date} style={{background:T.primary,color:"#fff",border:"none",borderRadius:9,padding:"10px 22px",fontSize:13,fontWeight:700,cursor:"pointer",opacity:!form.title||!form.date?0.5:1,fontFamily:"inherit"}}>Save Event</button>
      </div>
    </div>
  );
}

// ─── EVENT DETAIL ─────────────────────────────────────────────────────────────
function EventDetail({event,onEdit,onDelete,onClose,T}){
  const confirmed = (event.guests||[]).filter(g=>g.rsvp==="confirmed").length;
  const revenue   = event.pricePerGuest ? Number(event.pricePerGuest)*confirmed : 0;
  const pill = (label,color) => <span style={{background:color+"22",color,border:`1px solid ${color}44`,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700}}>{label}</span>;
  const infoCard = (label,val) => <div style={{background:T.statBg,border:`1px solid ${T.border}`,borderRadius:10,padding:"11px 14px"}}><div style={{fontSize:11,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3}}>{label}</div><div style={{fontSize:13,fontWeight:700,color:T.text}}>{val}</div></div>;
  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:18}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:T.text,fontFamily:"'Cormorant Garamond',serif",letterSpacing:"-0.3px",marginBottom:4}}>{event.title}</div>
          <div style={{fontSize:13,color:T.textMuted}}>{fmtDate(event.date)} · {event.startTime}–{event.endTime}</div>
        </div>
        {pill(event.status==="confirmed"?"✓ Confirmed":"Pending",event.status==="confirmed"?T.success:T.warn)}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:18}}>
        {[["Meal",event.mealType],["Cuisine",event.cuisine],["Seating",event.seating],["Price",event.pricePerGuest?`$${event.pricePerGuest}/guest`:"Complimentary"]].map(([l,v])=>infoCard(l,v))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:18}}>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:T.textSub,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>Guest Summary</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
            {[[confirmed,"Confirmed",T.success],[(event.guests||[]).length-confirmed,"Pending",T.warn],[event.maxGuests-(event.guests||[]).length,"Open",T.primary]].map(([v,l,c])=>(
              <div key={l} style={{background:c+"11",border:`1px solid ${c}33`,borderRadius:10,padding:"10px 0",textAlign:"center"}}>
                <div style={{fontSize:22,fontWeight:800,color:c}}>{v}</div>
                <div style={{fontSize:10,color:T.textMuted}}>{l}</div>
              </div>
            ))}
          </div>
          {revenue>0&&<div style={{background:T.gold+"11",border:`1px solid ${T.gold}33`,borderRadius:10,padding:"10px 14px",textAlign:"center"}}><div style={{fontSize:11,color:T.textMuted,marginBottom:2}}>Projected Revenue</div><div style={{fontSize:22,fontWeight:800,color:T.gold}}>${revenue}</div></div>}
        </div>
        {event.menuItems?.length>0&&(
          <div>
            <div style={{fontSize:12,fontWeight:700,color:T.textSub,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>Menu</div>
            {event.menuItems.map((item,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:7}}>
                <div style={{width:20,height:20,borderRadius:"50%",background:T.primary,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#fff",fontWeight:700,flexShrink:0}}>{i+1}</div>
                <span style={{fontSize:13,color:T.text}}>{item}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {(event.guests||[]).length>0&&(
        <div style={{marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:T.textSub,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>Guests</div>
          {(event.guests||[]).map(g=>(
            <div key={g.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:T.surface,borderRadius:10,marginBottom:7,border:`1px solid ${T.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:32,height:32,borderRadius:"50%",background:g.avatar,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:"#fff"}}>{getInitials(g.name)}</div>
                <div>
                  <div style={{fontWeight:600,fontSize:14,color:T.text}}>{g.name}</div>
                  {g.dietary.length>0&&<div style={{display:"flex",gap:4,marginTop:2}}>{g.dietary.map(d=><span key={d} style={{background:T.gold+"22",color:T.gold,border:`1px solid ${T.gold}44`,borderRadius:20,padding:"2px 7px",fontSize:10,fontWeight:700}}>{d}</span>)}</div>}
                </div>
              </div>
              <span style={{background:g.rsvp==="confirmed"?T.success+"22":T.warn+"22",color:g.rsvp==="confirmed"?T.success:T.warn,border:`1px solid ${g.rsvp==="confirmed"?T.success+"55":T.warn+"55"}`,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700}}>{g.rsvp==="confirmed"?"✓ Confirmed":"Pending"}</span>
            </div>
          ))}
        </div>
      )}
      {event.notes&&<div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 16px",marginBottom:16}}><div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>Host Notes</div><div style={{fontSize:13,lineHeight:1.6,color:T.text}}>{event.notes}</div></div>}
      {event.dietary?.length>0&&<div style={{marginBottom:16}}><div style={{fontSize:11,color:T.textMuted,marginBottom:6}}>Accommodations Offered</div><div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{event.dietary.map(d=><span key={d} style={{background:T.gold+"22",color:T.gold,border:`1px solid ${T.gold}44`,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:600}}>{d}</span>)}</div></div>}
      <div style={{height:1,background:T.border,margin:"16px 0"}} />
      <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
        <button onClick={()=>{onDelete(event.id);onClose();}} style={{background:"transparent",border:`1px solid ${T.danger}44`,borderRadius:9,padding:"9px 16px",fontSize:12,fontWeight:700,color:T.danger,cursor:"pointer",fontFamily:"inherit"}}>Delete</button>
        <div style={{display:"flex",gap:10}}>
          <button onClick={onClose} style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 18px",fontSize:13,fontWeight:600,color:T.textSub,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
          <button onClick={()=>onEdit(event)} style={{background:T.primary,color:"#fff",border:"none",borderRadius:9,padding:"9px 22px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Edit Event</button>
        </div>
      </div>
    </div>
  );
}

// ─── GUESTS CRUD TAB ─────────────────────────────────────────────────────────
const BLANK_GUEST = { name:"", email:"", phone:"", dietary:[], note:"", rsvp:"pending" };

function GuestFormFields({ form, setForm, T }) {
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const lbl = t => <label style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:5}}>{t}</label>;
  const inp = (k,ph,type="text") => (
    <input type={type} style={{width:"100%",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 13px",color:T.text,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"inherit"}}
      value={form[k]} onChange={e=>set(k,e.target.value)} placeholder={ph} />
  );
  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div>{lbl("Full Name *")}{inp("name","Jane Smith")}</div>
        <div>{lbl("Email")}{inp("email","jane@email.com","email")}</div>
        <div>{lbl("Phone")}{inp("phone","+1 (555) 000-0000","tel")}</div>
        <div>
          {lbl("RSVP Status")}
          <select style={{width:"100%",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 13px",color:T.text,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"inherit",cursor:"pointer"}} value={form.rsvp} onChange={e=>set("rsvp",e.target.value)}>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
          </select>
        </div>
      </div>
      <div style={{marginBottom:12}}>
        {lbl("Note")}
        <input style={{width:"100%",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 13px",color:T.text,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"inherit"}} value={form.note} onChange={e=>set("note",e.target.value)} placeholder="VIP, close friend, brings wine…" />
      </div>
      <div>
        {lbl("Dietary Restrictions")}
        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
          {DIETARY.map(d=>{
            const on=form.dietary.includes(d);
            return <span key={d} onClick={()=>set("dietary",on?form.dietary.filter(x=>x!==d):[...form.dietary,d])} style={{padding:"4px 11px",borderRadius:20,fontSize:12,fontWeight:500,cursor:"pointer",border:`1px solid ${on?T.chipBorder:T.border}`,background:on?T.chipActive:"transparent",color:on?T.chipText:T.textMuted,userSelect:"none"}}>{d}</span>;
          })}
        </div>
      </div>
    </div>
  );
}

function GuestsCRUD({ events, setEvents, T, dark }) {
  const AVAT = dark ? AVATAR_COLS_DARK : AVATAR_COLS_LIGHT;

  // flat list with eventId reference
  const allGuests = events.flatMap(ev =>
    (ev.guests||[]).map(g => ({ ...g, eventId: ev.id, eventTitle: ev.title, eventDate: ev.date }))
  );

  const [search, setSearch]         = useState("");
  const [filterEvent, setFilterEvent] = useState("all");
  const [filterRsvp, setFilterRsvp]   = useState("all");
  const [showAdd, setShowAdd]         = useState(false);
  const [addEventId, setAddEventId]   = useState(events[0]?.id || "");
  const [addForm, setAddForm]         = useState({...BLANK_GUEST});
  const [editGuest, setEditGuest]     = useState(null); // { guest, eventId }
  const [editForm, setEditForm]       = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { guestId, eventId, name }

  // helpers to mutate guests inside events
  const updateGuestInEvent = (eventId, guestId, updater) =>
    setEvents(prev => prev.map(ev => ev.id === eventId
      ? { ...ev, guests: (ev.guests||[]).map(g => g.id === guestId ? updater(g) : g) }
      : ev
    ));

  const removeGuestFromEvent = (eventId, guestId) =>
    setEvents(prev => prev.map(ev => ev.id === eventId
      ? { ...ev, guests: (ev.guests||[]).filter(g => g.id !== guestId) }
      : ev
    ));

  const addGuestToEvent = (eventId, guestData) => {
    const ev = events.find(e => e.id === eventId);
    if (!ev || (ev.guests||[]).length >= ev.maxGuests) return false;
    const idx = (ev.guests||[]).length;
    setEvents(prev => prev.map(e => e.id === eventId
      ? { ...e, guests: [...(e.guests||[]), { ...guestData, id: genId(), avatar: AVAT[idx % AVAT.length], addedAt: new Date().toISOString() }] }
      : e
    ));
    return true;
  };

  // filtered view
  const filtered = allGuests.filter(g => {
    const q = search.toLowerCase();
    const matchQ = !q || g.name.toLowerCase().includes(q) || (g.email||"").toLowerCase().includes(q) || (g.phone||"").includes(q);
    const matchEv = filterEvent === "all" || g.eventId === filterEvent;
    const matchRsvp = filterRsvp === "all" || g.rsvp === filterRsvp;
    return matchQ && matchEv && matchRsvp;
  });

  const confirmedCount = allGuests.filter(g=>g.rsvp==="confirmed").length;
  const pendingCount   = allGuests.filter(g=>g.rsvp==="pending").length;

  const btn = (label, onClick, variant="ghost", disabled=false) => {
    const bg = variant==="primary" ? T.primary : variant==="danger" ? T.danger : "transparent";
    const col = variant==="primary"||variant==="danger" ? "#fff" : T.textSub;
    const bord = variant==="ghost" ? `1px solid ${T.border}` : "none";
    return <button onClick={onClick} disabled={disabled} style={{background:bg,color:col,border:bord,borderRadius:9,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:disabled?"not-allowed":"pointer",opacity:disabled?0.5:1,fontFamily:"inherit",whiteSpace:"nowrap"}}>{label}</button>;
  };

  const inp = (style={}) => ({width:"100%",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 13px",color:T.text,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"inherit",...style});

  return (
    <div>
      {/* Header row */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:T.text,fontFamily:"'Cormorant Garamond',serif",marginBottom:3}}>Guest Directory</div>
          <div style={{fontSize:13,color:T.textMuted}}>{allGuests.length} guests across {events.length} events</div>
        </div>
        <button onClick={()=>{setShowAdd(v=>!v);setAddForm({...BLANK_GUEST});}} style={{background:T.primary,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
          {showAdd ? "✕ Cancel" : "+ Add Guest"}
        </button>
      </div>

      {/* Stats row */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        {[[allGuests.length,"Total Guests",T.primary],[confirmedCount,"Confirmed",T.success],[pendingCount,"Pending",T.warn],[events.reduce((s,e)=>s+Math.max(0,e.maxGuests-(e.guests||[]).length),0),"Open Seats",T.gold]].map(([v,l,c])=>(
          <div key={l} style={{background:T.statBg,border:`1px solid ${T.statBorder}`,borderRadius:12,padding:"14px 16px",textAlign:"center"}}>
            <div style={{fontSize:26,fontWeight:800,color:c,fontFamily:"'Cormorant Garamond',serif"}}>{v}</div>
            <div style={{fontSize:11,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em"}}>{l}</div>
          </div>
        ))}
      </div>

      {/* Add Guest Panel */}
      {showAdd && (
        <div style={{background:T.card,border:`1.5px solid ${T.primary}44`,borderRadius:16,padding:24,marginBottom:20,boxShadow:T.shadowMd}}>
          <div style={{fontSize:16,fontWeight:800,color:T.primary,fontFamily:"'Cormorant Garamond',serif",marginBottom:16}}>New Guest</div>
          <div style={{marginBottom:14}}>
            <label style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:5}}>Add to Event *</label>
            <select style={inp()} value={addEventId} onChange={e=>setAddEventId(e.target.value)}>
              {events.map(ev=>{
                const seats=ev.maxGuests-(ev.guests||[]).length;
                return <option key={ev.id} value={ev.id} disabled={seats<=0}>{ev.title} ({seats} seat{seats!==1?"s":""} left)</option>;
              })}
            </select>
          </div>
          <GuestFormFields form={addForm} setForm={setAddForm} T={T} />
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:18}}>
            {btn("Cancel",()=>setShowAdd(false))}
            {btn("Add Guest",()=>{
              if(!addForm.name.trim()||!addEventId) return;
              const ok=addGuestToEvent(addEventId,{...addForm});
              if(ok){setShowAdd(false);setAddForm({...BLANK_GUEST});}
            },"primary",!addForm.name.trim()||!addEventId)}
          </div>
        </div>
      )}

      {/* Search + Filter bar */}
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <input style={{...inp(),flex:1,minWidth:180}} placeholder="Search by name, email, phone…" value={search} onChange={e=>setSearch(e.target.value)} />
        <select style={{...inp(),width:"auto",minWidth:160}} value={filterEvent} onChange={e=>setFilterEvent(e.target.value)}>
          <option value="all">All Events</option>
          {events.map(ev=><option key={ev.id} value={ev.id}>{ev.title}</option>)}
        </select>
        <select style={{...inp(),width:"auto",minWidth:130}} value={filterRsvp} onChange={e=>setFilterRsvp(e.target.value)}>
          <option value="all">All RSVPs</option>
          <option value="confirmed">Confirmed</option>
          <option value="pending">Pending</option>
        </select>
        {(search||filterEvent!=="all"||filterRsvp!=="all")&&<button onClick={()=>{setSearch("");setFilterEvent("all");setFilterRsvp("all");}} style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 14px",fontSize:12,color:T.textMuted,cursor:"pointer",fontFamily:"inherit"}}>Clear</button>}
      </div>

      {/* Result count */}
      {(search||filterEvent!=="all"||filterRsvp!=="all")&&(
        <div style={{fontSize:12,color:T.textMuted,marginBottom:12}}>{filtered.length} result{filtered.length!==1?"s":""} found</div>
      )}

      {/* Guest list */}
      {filtered.length===0 && (
        <div style={{textAlign:"center",padding:"48px 32px",border:`1.5px dashed ${T.border}`,borderRadius:16,color:T.textMuted}}>
          <div style={{fontSize:40,marginBottom:10}}>👤</div>
          <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:6,fontFamily:"'Cormorant Garamond',serif"}}>{allGuests.length===0?"No guests yet":"No results"}</div>
          <div style={{fontSize:13}}>{allGuests.length===0?"Add guests to your events to see them here":"Try adjusting your search or filters"}</div>
        </div>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {filtered.map(g => (
          <div key={g.id+g.eventId} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"16px 20px",display:"flex",alignItems:"center",gap:14,boxShadow:T.shadow}}>
            {/* Avatar */}
            <div style={{width:44,height:44,borderRadius:"50%",background:g.avatar,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:700,color:"#fff",flexShrink:0}}>{getInitials(g.name)}</div>

            {/* Info */}
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:700,fontSize:15,color:T.text,fontFamily:"'Cormorant Garamond',serif",marginBottom:1}}>{g.name}</div>
              <div style={{fontSize:12,color:T.textMuted,marginBottom:4}}>{[g.email,g.phone].filter(Boolean).join(" · ")||"No contact"}</div>
              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                <span style={{fontSize:11,color:T.textSub}}>📅 {g.eventTitle}</span>
                {g.note&&<span style={{fontSize:11,color:T.textMuted,fontStyle:"italic"}}>· {g.note}</span>}
              </div>
              {g.dietary?.length>0&&(
                <div style={{display:"flex",gap:4,marginTop:5,flexWrap:"wrap"}}>
                  {g.dietary.map(d=><span key={d} style={{background:T.gold+"22",color:T.gold,border:`1px solid ${T.gold}44`,borderRadius:20,padding:"2px 8px",fontSize:10,fontWeight:700}}>{d}</span>)}
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
              {/* RSVP toggle */}
              <span onClick={()=>updateGuestInEvent(g.eventId,g.id,x=>({...x,rsvp:x.rsvp==="confirmed"?"pending":"confirmed"}))}
                style={{cursor:"pointer",background:g.rsvp==="confirmed"?T.success+"22":T.warn+"22",color:g.rsvp==="confirmed"?T.success:T.warn,border:`1px solid ${g.rsvp==="confirmed"?T.success+"55":T.warn+"55"}`,borderRadius:20,padding:"4px 12px",fontSize:11,fontWeight:700,userSelect:"none"}}
                title="Click to toggle RSVP">
                {g.rsvp==="confirmed"?"✓ Confirmed":"Pending"}
              </span>

              {/* Edit */}
              <button onClick={()=>{setEditGuest({guestId:g.id,eventId:g.eventId});setEditForm({name:g.name,email:g.email,phone:g.phone,dietary:[...g.dietary],note:g.note||"",rsvp:g.rsvp});}}
                style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:9,padding:"6px 14px",fontSize:12,fontWeight:600,color:T.textSub,cursor:"pointer",fontFamily:"inherit"}}>
                Edit
              </button>

              {/* Delete */}
              <button onClick={()=>setDeleteTarget({guestId:g.id,eventId:g.eventId,name:g.name})}
                style={{background:"transparent",border:`1px solid ${T.danger}44`,borderRadius:9,padding:"6px 12px",fontSize:12,fontWeight:700,color:T.danger,cursor:"pointer",fontFamily:"inherit"}}>
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── EDIT MODAL ── */}
      {editGuest && editForm && (
        <div onClick={e=>e.target===e.currentTarget&&setEditGuest(null)} style={{position:"fixed",inset:0,background:T.modalOverlay,display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,padding:24}}>
          <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:20,padding:32,width:"100%",maxWidth:520,boxShadow:T.shadowMd}}>
            <div style={{fontSize:20,fontWeight:800,color:T.text,fontFamily:"'Cormorant Garamond',serif",marginBottom:4}}>Edit Guest</div>
            <div style={{fontSize:13,color:T.textMuted,marginBottom:20}}>
              {events.find(e=>e.id===editGuest.eventId)?.title}
            </div>
            <GuestFormFields form={editForm} setForm={setEditForm} T={T} />
            <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:22}}>
              {btn("Cancel",()=>setEditGuest(null))}
              {btn("Save Changes",()=>{
                if(!editForm.name.trim()) return;
                updateGuestInEvent(editGuest.eventId,editGuest.guestId,g=>({...g,...editForm}));
                setEditGuest(null);
              },"primary",!editForm.name.trim())}
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ── */}
      {deleteTarget && (
        <div onClick={e=>e.target===e.currentTarget&&setDeleteTarget(null)} style={{position:"fixed",inset:0,background:T.modalOverlay,display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,padding:24}}>
          <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:20,padding:32,width:"100%",maxWidth:420,textAlign:"center",boxShadow:T.shadowMd}}>
            <div style={{fontSize:44,marginBottom:12}}>🗑</div>
            <div style={{fontSize:18,fontWeight:800,color:T.text,fontFamily:"'Cormorant Garamond',serif",marginBottom:8}}>Remove Guest?</div>
            <div style={{fontSize:14,color:T.textMuted,marginBottom:24,lineHeight:1.6}}>
              <strong style={{color:T.text}}>{deleteTarget.name}</strong> will be removed from this event. This cannot be undone.
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              {btn("Cancel",()=>setDeleteTarget(null))}
              <button onClick={()=>{removeGuestFromEvent(deleteTarget.eventId,deleteTarget.guestId);setDeleteTarget(null);}}
                style={{background:T.danger,color:"#fff",border:"none",borderRadius:9,padding:"10px 22px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                Yes, Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App(){
  const [dark,setDark]   = useState(false);
  const T                = dark ? DARK : LIGHT;
  const [events,setEvents] = useState(()=>mkEvents(dark));
  const [tab,setTab]     = useState("dashboard");
  const [modal,setModal] = useState(null);
  const [selected,setSelected] = useState(null);
  const [selDate,setSelDate]   = useState(null);
  const [viewYear,setViewYear] = useState(today.getFullYear());
  const [viewMonth,setViewMonth] = useState(today.getMonth());

  const saveEvent = ev => { setEvents(p=>p.find(e=>e.id===ev.id)?p.map(e=>e.id===ev.id?ev:e):[...p,ev]); setModal(null); setSelected(null); };
  const deleteEvent = id => setEvents(p=>p.filter(e=>e.id!==id));
  const openDetail  = ev => { setSelected(ev); setModal("detail"); };
  const openEdit    = ev => { setSelected(ev); setModal("edit"); };
  const openCreate  = (date) => { setSelected(date?{date}:null); setModal("create"); };

  const upcoming     = events.filter(e=>e.date>=todayStr).sort((a,b)=>a.date.localeCompare(b.date));
  const totalGuests  = events.reduce((s,e)=>s+(e.guests||[]).length,0);
  const confirmed    = events.reduce((s,e)=>s+(e.guests||[]).filter(g=>g.rsvp==="confirmed").length,0);
  const revenue      = events.reduce((s,e)=>s+(e.pricePerGuest?Number(e.pricePerGuest)*(e.guests||[]).filter(g=>g.rsvp==="confirmed").length:0),0);

  const navTab = (id,label) => (
    <button key={id} onClick={()=>setTab(id)} style={{padding:"6px 16px",borderRadius:20,border:"none",cursor:"pointer",fontSize:14,fontWeight:600,background:tab===id?T.primary:T.surface,color:tab===id?"#fff":T.textSub,transition:"all 0.2s",fontFamily:"inherit",boxShadow:tab===id?T.shadow:"none"}}>
      {label}
    </button>
  );

  const pillTag = (txt,color) => <span style={{display:"inline-flex",alignItems:"center",background:T.surface,border:`1px solid ${T.border}`,borderRadius:20,padding:"3px 11px",fontSize:11,color:T.textSub}}>{txt}</span>;

  return(
    <div style={{background:T.bg,minHeight:"100vh",fontFamily:"'DM Sans',sans-serif",color:T.text,transition:"background 0.3s,color 0.3s"}}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Cormorant+Garamond:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* NAV */}
      <nav style={{background:T.surface,borderBottom:`1px solid ${T.navBorder}`,padding:"0 32px",display:"flex",alignItems:"center",justifyContent:"space-between",height:64,position:"sticky",top:0,zIndex:100,boxShadow:T.shadow}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:36,height:36,borderRadius:10,background:T.primary,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>🌿</div>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:T.text,fontFamily:"'Cormorant Garamond',serif",letterSpacing:"-0.2px",lineHeight:1}}>Her Supper Club</div>
            <div style={{fontSize:10,color:T.textMuted,letterSpacing:"0.1em",textTransform:"uppercase"}}>Private Dining Scheduler</div>
          </div>
        </div>
        <div style={{display:"flex",gap:6}}>
          {[["dashboard","Dashboard"],["calendar","Calendar"],["events","Events"],["guests","Guests"]].map(([id,l])=>navTab(id,l))}
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <ThemeToggle dark={dark} onToggle={()=>setDark(d=>!d)} T={T} />
          <button onClick={()=>openCreate()} style={{background:T.primary,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ New Event</button>
        </div>
      </nav>

      <div style={{maxWidth:1200,margin:"0 auto",padding:"32px 24px"}}>

        {/* DASHBOARD */}
        {tab==="dashboard"&&(
          <div>
            {/* HERO */}
            <div style={{background:T.heroBg,borderRadius:20,padding:"44px 52px",marginBottom:28,border:`1px solid ${T.border}`,boxShadow:T.shadowMd,position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",right:48,top:"50%",transform:"translateY(-50%)",fontSize:110,opacity:dark?0.05:0.08}}>🌿</div>
              {!dark&&<div style={{position:"absolute",top:0,right:0,width:320,height:"100%",background:"linear-gradient(135deg,#EDF4F0 0%,transparent 60%)",borderRadius:"0 20px 20px 0"}} />}
              <div style={{fontSize:13,fontWeight:700,color:T.primary,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:10}}>Welcome back</div>
              <div style={{fontSize:40,fontWeight:800,lineHeight:1.1,marginBottom:10,letterSpacing:"-0.5px",fontFamily:"'Cormorant Garamond',serif",color:T.text}}>Your Private<br />Dining Hub</div>
              <div style={{fontSize:16,color:T.textSub,marginBottom:28,maxWidth:440,lineHeight:1.6}}>Schedule intimate meals, manage guests, and craft unforgettable culinary experiences.</div>
              <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
                {[["Events Scheduled",events.length,T.primary],["Total Guests",totalGuests,T.accent],["Confirmed RSVPs",confirmed,T.success],["Est. Revenue",`$${revenue}`,T.gold]].map(([l,v,c])=>(
                  <div key={l} style={{background:dark?"rgba(255,255,255,0.07)":"rgba(255,255,255,0.75)",border:`1px solid ${T.border}`,borderRadius:12,padding:"12px 20px",backdropFilter:"blur(4px)"}}>
                    <div style={{fontSize:24,fontWeight:800,color:c,fontFamily:"'Cormorant Garamond',serif"}}>{v}</div>
                    <div style={{fontSize:11,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em"}}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{fontSize:18,fontWeight:800,color:T.text,fontFamily:"'Cormorant Garamond',serif",marginBottom:16}}>Upcoming Events</div>
            {upcoming.length===0&&(
              <div style={{textAlign:"center",padding:"48px 32px",border:`1.5px dashed ${T.border}`,borderRadius:16,color:T.textMuted}}>
                <div style={{fontSize:48,marginBottom:10}}>🍽</div>
                <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:6,fontFamily:"'Cormorant Garamond',serif"}}>No upcoming events</div>
                <div style={{fontSize:13,marginBottom:20}}>Schedule your first dinner to get started</div>
                <button onClick={()=>openCreate()} style={{background:T.primary,color:"#fff",border:"none",borderRadius:10,padding:"11px 24px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Create Event</button>
              </div>
            )}
            {upcoming.map(ev=>{
              const c=(ev.guests||[]).filter(g=>g.rsvp==="confirmed").length;
              return(
                <div key={ev.id} onClick={()=>openDetail(ev)} style={{background:T.card,border:`1px solid ${ev.status==="confirmed"?T.success+"44":T.border}`,borderRadius:16,padding:22,marginBottom:14,cursor:"pointer",transition:"box-shadow 0.15s",boxShadow:T.shadow}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                    <div>
                      <div style={{fontSize:17,fontWeight:800,color:T.text,fontFamily:"'Cormorant Garamond',serif",marginBottom:3}}>{ev.title}</div>
                      <div style={{fontSize:13,color:T.textMuted,marginBottom:10}}>{fmtDate(ev.date)} · {ev.startTime}–{ev.endTime}</div>
                      <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                        {[ev.mealType,ev.cuisine,ev.seating,ev.pricePerGuest&&`$${ev.pricePerGuest}/guest`].filter(Boolean).map(t=><span key={t} style={{display:"inline-flex",background:T.surface,border:`1px solid ${T.border}`,borderRadius:20,padding:"3px 11px",fontSize:11,color:T.textSub}}>{t}</span>)}
                      </div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0,marginLeft:16}}>
                      <span style={{background:ev.status==="confirmed"?T.success+"22":T.warn+"22",color:ev.status==="confirmed"?T.success:T.warn,border:`1px solid ${ev.status==="confirmed"?T.success+"55":T.warn+"55"}`,borderRadius:20,padding:"4px 12px",fontSize:12,fontWeight:700}}>{ev.status==="confirmed"?"✓ Confirmed":"Pending"}</span>
                      <div style={{display:"flex",marginTop:10,justifyContent:"flex-end"}}>
                        {(ev.guests||[]).slice(0,5).map((g,i)=>(
                          <div key={g.id} style={{width:28,height:28,borderRadius:"50%",background:g.avatar,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#fff",marginLeft:i===0?0:-8,border:`2px solid ${T.card}`}}>{getInitials(g.name)}</div>
                        ))}
                      </div>
                      <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>{c}/{ev.maxGuests} seats</div>
                    </div>
                  </div>
                  {ev.menuItems?.length>0&&<div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${T.border}`,display:"flex",gap:14,flexWrap:"wrap"}}>{ev.menuItems.slice(0,4).map((item,i)=><span key={i} style={{fontSize:11,color:T.textMuted}}>🍴 {item}</span>)}{ev.menuItems.length>4&&<span style={{fontSize:11,color:T.textMuted}}>+{ev.menuItems.length-4} more</span>}</div>}
                </div>
              );
            })}
          </div>
        )}

        {/* CALENDAR */}
        {tab==="calendar"&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div>
                <div style={{fontSize:22,fontWeight:800,color:T.text,fontFamily:"'Cormorant Garamond',serif",marginBottom:3}}>Calendar</div>
                <div style={{fontSize:13,color:T.textMuted}}>Click any future day to schedule</div>
              </div>
              <button onClick={()=>openCreate(selDate)} style={{background:T.primary,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ New on Selected Day</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 320px",gap:24}}>
              <CalendarView events={events} selectedDate={selDate} setSelectedDate={setSelDate} viewYear={viewYear} viewMonth={viewMonth} setViewMonth={setViewMonth} setViewYear={setViewYear} T={T} dark={dark} />
              <div>
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:20,marginBottom:16,boxShadow:T.shadow}}>
                  <div style={{fontWeight:700,fontSize:14,color:T.primary,fontFamily:"'Cormorant Garamond',serif",marginBottom:14}}>{selDate?fmtDate(selDate):"Select a Day"}</div>
                  {selDate?(()=>{
                    const dayEvs=events.filter(e=>e.date===selDate);
                    return dayEvs.length===0?(
                      <div style={{textAlign:"center",padding:"18px 0",color:T.textMuted}}>
                        <div style={{fontSize:28,marginBottom:7}}>✨</div>
                        <div style={{fontSize:13,marginBottom:12}}>No events this day</div>
                        <button onClick={()=>openCreate(selDate)} style={{background:T.primary,color:"#fff",border:"none",borderRadius:9,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Schedule Here</button>
                      </div>
                    ):dayEvs.map(ev=>(
                      <div key={ev.id} onClick={()=>openDetail(ev)} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:12,marginBottom:8,cursor:"pointer"}}>
                        <div style={{fontWeight:700,fontSize:14,color:T.text,marginBottom:2}}>{ev.title}</div>
                        <div style={{fontSize:12,color:T.textMuted}}>{ev.startTime} · {(ev.guests||[]).length}/{ev.maxGuests} guests</div>
                      </div>
                    ));
                  })():<div style={{color:T.textMuted,fontSize:13}}>Click a date on the calendar to see events</div>}
                </div>
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:20,boxShadow:T.shadow}}>
                  <div style={{fontWeight:700,fontSize:14,color:T.primary,fontFamily:"'Cormorant Garamond',serif",marginBottom:12}}>This Month</div>
                  {[["Events",events.filter(e=>{const d=new Date(e.date+"T12:00:00");return d.getFullYear()===viewYear&&d.getMonth()===viewMonth;}).length],["Guests",events.filter(e=>{const d=new Date(e.date+"T12:00:00");return d.getFullYear()===viewYear&&d.getMonth()===viewMonth;}).reduce((s,e)=>s+(e.guests||[]).length,0)],["Revenue","$"+events.filter(e=>{const d=new Date(e.date+"T12:00:00");return d.getFullYear()===viewYear&&d.getMonth()===viewMonth;}).reduce((s,e)=>s+(e.pricePerGuest?Number(e.pricePerGuest)*(e.guests||[]).filter(g=>g.rsvp==="confirmed").length:0),0)]].map(([l,v])=>(
                    <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${T.border}33`}}>
                      <span style={{fontSize:13,color:T.textMuted}}>{l}</span>
                      <span style={{fontSize:13,fontWeight:700,color:T.text}}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* EVENTS */}
        {tab==="events"&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div>
                <div style={{fontSize:22,fontWeight:800,color:T.text,fontFamily:"'Cormorant Garamond',serif",marginBottom:3}}>All Events</div>
                <div style={{fontSize:13,color:T.textMuted}}>{events.length} events total</div>
              </div>
              <button onClick={()=>openCreate()} style={{background:T.primary,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ New Event</button>
            </div>
            {events.length===0&&<div style={{textAlign:"center",color:T.textMuted,padding:48}}>No events yet</div>}
            {events.sort((a,b)=>a.date.localeCompare(b.date)).map(ev=>{
              const c=(ev.guests||[]).filter(g=>g.rsvp==="confirmed").length;
              const revv=ev.pricePerGuest?Number(ev.pricePerGuest)*c:0;
              return(
                <div key={ev.id} onClick={()=>openDetail(ev)} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 22px",marginBottom:12,cursor:"pointer",boxShadow:T.shadow,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div style={{display:"flex",alignItems:"center",gap:16}}>
                    <div style={{width:50,height:50,borderRadius:12,background:T.primary+"22",border:`1px solid ${T.primary}44`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase"}}>{new Date(ev.date+"T12:00:00").toLocaleDateString("en-US",{month:"short"})}</div>
                      <div style={{fontSize:20,fontWeight:800,color:T.primary,lineHeight:1,fontFamily:"'Cormorant Garamond',serif"}}>{new Date(ev.date+"T12:00:00").getDate()}</div>
                    </div>
                    <div>
                      <div style={{fontSize:15,fontWeight:800,color:T.text,fontFamily:"'Cormorant Garamond',serif",marginBottom:2}}>{ev.title}</div>
                      <div style={{fontSize:12,color:T.textMuted}}>{ev.mealType} · {ev.cuisine} · {ev.startTime}–{ev.endTime}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:20,alignItems:"center"}}>
                    <div style={{textAlign:"center"}}><div style={{fontSize:18,fontWeight:800,color:c>=ev.maxGuests?T.danger:T.primary,fontFamily:"'Cormorant Garamond',serif"}}>{c}/{ev.maxGuests}</div><div style={{fontSize:10,color:T.textMuted}}>Confirmed</div></div>
                    {revv>0&&<div style={{textAlign:"center"}}><div style={{fontSize:18,fontWeight:800,color:T.gold,fontFamily:"'Cormorant Garamond',serif"}}>${revv}</div><div style={{fontSize:10,color:T.textMuted}}>Revenue</div></div>}
                    <span style={{background:ev.status==="confirmed"?T.success+"22":T.warn+"22",color:ev.status==="confirmed"?T.success:T.warn,border:`1px solid ${ev.status==="confirmed"?T.success+"55":T.warn+"55"}`,borderRadius:20,padding:"4px 12px",fontSize:11,fontWeight:700}}>{ev.status}</span>
                    <button onClick={e=>{e.stopPropagation();openEdit(ev);}} style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:9,padding:"7px 14px",fontSize:12,fontWeight:600,color:T.textSub,cursor:"pointer",fontFamily:"inherit"}}>Edit</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* GUESTS CRUD TAB */}
        {tab==="guests"&&(
          <GuestsCRUD events={events} setEvents={setEvents} T={T} dark={dark} />
        )}
      </div>

      {/* MODAL */}
      {modal&&(
        <div onClick={e=>e.target===e.currentTarget&&setModal(null)} style={{position:"fixed",inset:0,background:T.modalOverlay,display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:24}}>
          <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:20,padding:32,width:"100%",maxWidth:600,maxHeight:"90vh",overflowY:"auto",boxShadow:T.shadowMd}}>
            {modal==="detail"&&selected&&<EventDetail event={selected} onEdit={ev=>{setSelected(ev);setModal("edit");}} onDelete={deleteEvent} onClose={()=>{setModal(null);setSelected(null);}} T={T} />}
            {(modal==="create"||modal==="edit")&&<EventForm initial={modal==="edit"?selected:(selected?.date?{date:selected.date}:null)} onSave={saveEvent} onCancel={()=>{setModal(null);setSelected(null);}} T={T} dark={dark} />}
          </div>
        </div>
      )}
    </div>
  );
}
